import moment from 'moment-timezone';
import { utcToZonedTime, format as formatInTimezone } from 'date-fns-tz';
import { format, startOfDay } from 'date-fns';
import { AbsoluteTimeRange, ReportingPeriod } from '@nirvana/shared/dtos/types/shared/time';
import { longToNumber } from '@nirvana/shared/basic-types/long';
import { TimeUnit } from '@nirvana/shared/constants';

function getRoundedTimestampToHour(timestamp: number, timezone: string) {
  /**
   * Get now rounded down to fixed hour -> set minutes, seconds and ms to 0.
   * Use moment instead of Date to set the minutes, seconds and ms. Using
   * setMinutes from Date sets these using the local timezone.
   */
  return moment(timestamp).tz(timezone).startOf('hour').valueOf();
}

export function getRoundedTimestampToMidnight(timestamp: number, timezone: string) {
  /**
   * Round down the value to midnight
   */
  return moment(timestamp).tz(timezone).startOf('day').valueOf();
}

export function timeRangeToReportingInterval(lastNumDays: number, timezone: string): ReportingPeriod {
  /**
   * The specs for this function can be found here:
   * https://bitdefender-bsg.atlassian.net/browse/CERES-11877
   */
  const now = Date.now();
  const previousDay = lastNumDays === 1;

  const timestamp = previousDay
    ? getRoundedTimestampToHour(now, timezone)
    : getRoundedTimestampToMidnight(now, timezone);

  return {
    current: {
      fromTimestamp: subtractDaysFromDate(timestamp, timezone, lastNumDays),
      toTimestamp: now,
    },
    previous: {
      fromTimestamp: subtractDaysFromDate(timestamp, timezone, lastNumDays * 2),
      toTimestamp: subtractDaysFromDate(timestamp, timezone, lastNumDays),
    },
    timeRange: lastNumDays,
  };
}

export function timeRangeToReportingIntervalFromTimestamp(timestampNow: number, lastNumDays: number, timezone: string): ReportingPeriod {
  const previousDay = lastNumDays === 1;

  const timestamp = previousDay
    ? getRoundedTimestampToHour(timestampNow, timezone)
    : getRoundedTimestampToMidnight(timestampNow, timezone);

  return {
    current: {
      fromTimestamp: subtractDaysFromDate(timestamp, timezone, lastNumDays),
      toTimestamp: timestampNow,
    },
    previous: {
      fromTimestamp: subtractDaysFromDate(timestamp, timezone, lastNumDays * 2),
      toTimestamp: subtractDaysFromDate(timestamp, timezone, lastNumDays),
    },
    timeRange: lastNumDays,
  };
}

function subtractDaysFromDate(timestamp: number, timezone: string, daysToSubtract: number): number {
  return moment(timestamp).tz(timezone).subtract(daysToSubtract, 'day').valueOf();
}

export function getComputedTimeRangeFromToday(lastNumDays: number, timezone: string): AbsoluteTimeRange {
  // get now as UTC timestamp
  const now = Date.now();
  const roundedToday = getRoundedTimestampToMidnight(now, timezone);
  const msPerNumDays = lastNumDays * 24 * 60 * 60 * 1000;

  return {
    fromTimestamp: roundedToday - msPerNumDays,
    toTimestamp: now,
  };
}

export function dateFormatter(
  timestamp: number | string,
  timezone: string,
  formatToken: string = 'D MMM YYYY HH:mm'
): string {
  if (timezone === '') {
    return moment(timestamp).format(formatToken);
  }

  return moment(timestamp).tz(timezone).format(formatToken);
}

export function jsTimestampToUnixTimestamp(timestamp: number): number {
  return Math.round(timestamp / 1000);
}

export function unixTimestampToJsTimestamp(timestamp: number): number {
  return Math.round(timestamp * 1000);
}

export function getHourFromTimestamp(timestamp: number, timezone: string): number {
  return parseInt(formatInTimezone(timestamp, 'H', { timeZone: timezone }), 10);
}

export function getMinuteFromTimestamp(timestamp: number, timezone: string): number {
  return parseInt(formatInTimezone(timestamp, 'm', { timeZone: timezone }), 10);
}

export function hoursFormatter(timestamp: number, timezone: string): string {
  return moment(timestamp).tz(timezone).format('HH');
}

export function minutesFormatter(timestamp: number, timezone: string): string {
  return moment(timestamp).tz(timezone).format('mm');
}

export function dayFormatter(timestamp: number, timezone: string, userLocale?: Locale): string {
  return formatInTimezone(timestamp, 'd MMM yyyy', { timeZone: timezone, locale: userLocale });
}

export function getFormattedFromAbsoluteTimeRange(timeRange: AbsoluteTimeRange): AbsoluteTimeRange {
  const { fromTimestamp, toTimestamp } = timeRange;

  return {
    fromTimestamp: typeof fromTimestamp === 'number' ? fromTimestamp : longToNumber(fromTimestamp),
    toTimestamp: typeof toTimestamp === 'number' ? toTimestamp : longToNumber(toTimestamp),
  };
}

export function getTimezonedCurrentDateTimestamp(timezone: string): number {
  const zonedDate = utcToZonedTime(Date.now(), timezone);
  return parseInt(format(startOfDay(zonedDate), 't'), 10);
}

export enum TimestampFormat {
  FILES = 'YYYY-MM-DTHH:mm:ss',
  REPORTS = 'D MMMM YYYY, HH:mm:ss',
  EVENTS = 'D MMMM YYYY, HH:mm',
  DATE_ONLY = 'D MMMM YYYY',
}

export function getFormattedDateTime(
  timestampFormat: TimestampFormat,
  timestamp: number,
  timezone: string,
  locale?: string
) {
  moment.locale(locale);

  return moment(timestamp).tz(timezone).format(timestampFormat);
}

export function formatDate(date: string): string {
  const formatted = new Date(date),
    day = formatted.getDate(),
    month = formatted.toLocaleString('default', { month: 'short' }),
    year = formatted.getFullYear(),
    hour = formatted.getHours();

  let minutes = '' + formatted.getMinutes();
  minutes = minutes.length === 1 ? '0' + minutes : minutes;

  return day + ' ' + month + ' ' + year + ', ' + hour + ':' + minutes;
}

export function convertTimestampToLong(timestamp: number): number {
  const high = timestamp / Math.pow(2, 32);
  const low = timestamp % Math.pow(2, 32);

  return {
    high: Math.floor(high),
    low: low,
    unsigned: false,
  } as unknown as number;
}
